// src/pages/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { ADMIN_EMAILS, IMGBB_API_KEY } from '../config';
import { 
  collection, query, orderBy, onSnapshot, doc, where,
  updateDoc, deleteDoc, setDoc, serverTimestamp, increment, getDoc 
} from 'firebase/firestore';
import { 
  ShieldCheck, Upload, Loader2, Trash2, CheckCircle, 
  RotateCcw, Eye, EyeOff, AlertTriangle, Star, Image as ImageIcon,
  ShoppingCart, MessageCircle, Flag, Search, Car, Mail, Clock, Pin, Zap, Tag
} from 'lucide-react';
import { getEmojiFlag } from '../utils/helpers';
import axios from 'axios';

export const AdminPanel = ({ sightings, showToast }) => {
  const isAdmin = auth.currentUser && ADMIN_EMAILS.includes(auth.currentUser.email);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('spotting');
  
  // Spotting state
  const [editCountry, setEditCountry] = useState(null);
  const [selectedSighting, setSelectedSighting] = useState(null);
  const [comments, setComments] = useState([]);
  const [editingVin, setEditingVin] = useState(null);
  const [vinValue, setVinValue] = useState("");
  
  // Marketplace state
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [listingMessages, setListingMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allConversations, setAllConversations] = useState([]);
  const [editingFlags, setEditingFlags] = useState(null);
  
  // Settings state
  const [logoFile, setLogoFile] = useState(null);
  const [watermarkFile, setWatermarkFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  const [siteName, setSiteName] = useState("CarWatch");
  const [termsContent, setTermsContent] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(true);
  
  // Load marketplace listings
  useEffect(() => {
    const q = query(collection(db, "marketplace_listings"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      setListings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);
  
  // Load all conversations for marketplace
  useEffect(() => {
    const q = query(collection(db, "marketplace_conversations"), orderBy("lastMessageAt", "desc"));
    return onSnapshot(q, (snap) => {
      setAllConversations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);
  
  // Load spotting comments
  useEffect(() => {
    if (selectedSighting) {
      const q = query(collection(db, "sightings", selectedSighting, "comments"), orderBy("createdAt", "desc"));
      const unsub = onSnapshot(q, (snap) => setComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
      return () => unsub();
    }
  }, [selectedSighting]);

  // Load listing messages and conversations
  useEffect(() => {
    if (selectedListing) {
      const unsubMessages = onSnapshot(collection(db, "marketplace_messages"), (snap) => {
        const allMessages = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered = allMessages.filter(m => m.listingId === selectedListing);
        setListingMessages(filtered.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        }));
      });
      
      return () => {
        unsubMessages();
      };
    } else {
      setListingMessages([]);
    }
  }, [selectedListing]);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, "settings", "site"));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setSiteName(data.name || "CarWatch");
          setTermsContent(data.terms || "");
          setSeoKeywords(data.seoKeywords || "");
          setSeoDescription(data.seoDescription || "");
        }
      } catch (error) {
        console.error("Błąd ładowania ustawień:", error);
      } finally {
        setLoadingSettings(false);
      }
    };
    loadSettings();
  }, []);
  
  if (!isAdmin) return <Navigate to="/login" />;

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('image', logoFile);
      const res = await axios.post(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, formData);
      const url = res.data.data.url;
      
      await setDoc(doc(db, "settings", "logo"), { url, updatedAt: serverTimestamp() });
      
      showToast("Logo zaktualizowane! Odśwież stronę.");
      setLogoFile(null);
    } catch (error) {
      showToast("Błąd uploadu logo", "error");
      console.error(error);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleWatermarkUpload = async () => {
    if (!watermarkFile) return;
    setUploadingWatermark(true);
    
    try {
      const formData = new FormData();
      formData.append('image', watermarkFile);
      const res = await axios.post(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, formData);
      const url = res.data.data.url;
      
      await setDoc(doc(db, "settings", "watermark"), { url, updatedAt: serverTimestamp() });
      
      showToast("Watermark zaktualizowany! Odśwież stronę.");
      setWatermarkFile(null);
    } catch (error) {
      showToast("Błąd uploadu watermark", "error");
      console.error(error);
    } finally {
      setUploadingWatermark(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await setDoc(doc(db, "settings", "site"), {
        name: siteName,
        terms: termsContent,
        seoKeywords: seoKeywords,
        seoDescription: seoDescription,
        updatedAt: serverTimestamp()
      });
      
      document.title = siteName;
      
      if (seoDescription) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.name = 'description';
          document.head.appendChild(metaDesc);
        }
        metaDesc.content = seoDescription;
      }
      
      if (seoKeywords) {
        let metaKeys = document.querySelector('meta[name="keywords"]');
        if (!metaKeys) {
          metaKeys = document.createElement('meta');
          metaKeys.name = 'keywords';
          document.head.appendChild(metaKeys);
        }
        metaKeys.content = seoKeywords;
      }
      
      showToast("Ustawienia zapisane!");
    } catch (error) {
      showToast("Błąd zapisywania ustawień", "error");
      console.error(error);
    }
  };

  const handleSpottingAction = async (id, act, val) => {
    const ref = doc(db, "sightings", id);
    if (act === 'del' && window.confirm("Czy na pewno usunąć?")) await deleteDoc(ref);
    if (act === 'ok') await updateDoc(ref, { status: 'verified', reported: false });
    if (act === 'undo') await updateDoc(ref, { status: 'pending', reported: false });
    if (act === 'hide') await updateDoc(ref, { hidden: !val });
    if (act === 'exclusive') await updateDoc(ref, { isExclusive: !val });
    if (act === 'alert') await updateDoc(ref, { isAlert: !val });
    if (act === 'pin') await updateDoc(ref, { isPinned: !val });
    if (act === 'country') {
        const code = val.toUpperCase();
        await updateDoc(ref, { countryCode: code, countryEmoji: getEmojiFlag(code) });
        setEditCountry(null);
    }
    if (act === 'vin') {
        await updateDoc(ref, { vin: vinValue.toUpperCase() });
        setEditingVin(null);
        setVinValue("");
    }
    showToast("Zaktualizowano");
  };

  const handleListingAction = async (id, act, val, listing) => {
    const ref = doc(db, "marketplace_listings", id);
    try {
      if (act === 'delete' && window.confirm("Czy na pewno usunąć ogłoszenie?")) {
        await deleteDoc(ref);
        showToast("Ogłoszenie usunięte");
      }
      if (act === 'activate') {
        await updateDoc(ref, { status: 'active', reported: false });
        showToast("Ogłoszenie aktywowane ✅");
      }
      if (act === 'activate_manual') {
        await updateDoc(ref, { 
          status: 'active', 
          reported: false,
          manuallyActivated: true,
          activatedBy: auth.currentUser.uid,
          activatedAt: serverTimestamp()
        });
        showToast("Ogłoszenie RĘCZNIE aktywowane przez admina ✅");
      }
      if (act === 'hide') {
        await updateDoc(ref, { status: val === 'active' ? 'hidden' : 'active' });
        showToast(val === 'active' ? "Ogłoszenie ukryte" : "Ogłoszenie pokazane");
      }
      if (act === 'flag') {
        await updateDoc(ref, { reported: true, reportReason: "Oznaczone przez admina" });
        showToast("Ogłoszenie oflagowane");
      }
      if (act === 'unflag') {
        await updateDoc(ref, { reported: false, reportReason: null });
        showToast("Flaga usunięta");
      }
      if (act === 'pin') {
        await updateDoc(ref, { isPinned: !val });
        showToast(val ? "Ogłoszenie odpięte" : "Ogłoszenie przypięte 📌");
      }
      if (act === 'toggle_flag') {
        const currentFlags = listing.flags || [];
        const newFlags = currentFlags.includes(val)
          ? currentFlags.filter(f => f !== val)
          : [...currentFlags, val];
        await updateDoc(ref, { flags: newFlags });
        showToast("Flagi zaktualizowane");
      }
    } catch (error) {
      console.error("Błąd akcji:", error);
      showToast("Błąd wykonywania akcji", "error");
    }
  };

  const deleteComment = async (sightingId, commentId) => {
    if (!window.confirm("Usunąć komentarz?")) return;
    await deleteDoc(doc(db, "sightings", sightingId, "comments", commentId));
    await updateDoc(doc(db, "sightings", sightingId), { commentCount: increment(-1) });
    showToast("Komentarz usunięty");
  };

  const deleteListingMessage = async (messageId) => {
    if (!window.confirm("Usunąć wiadomość?")) return;
    await deleteDoc(doc(db, "marketplace_messages", messageId));
    showToast("Wiadomość usunięta");
  };

  const filteredListings = listings.filter(l => 
    l.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getConversationCount = (listingId) => {
    return allConversations.filter(c => c.listingId === listingId).length;
  };

  if (loadingSettings) return <div className="p-10 text-center text-gray-400">Ładowanie panelu...</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 pb-32">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <ShieldCheck className="text-red-500" size={28}/>
        Panel Administratora
      </h1>

      {/* TABS */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6 p-1 flex gap-1">
        <button
          onClick={() => setActiveTab('spotting')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'spotting' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Car size={18}/>
          Spotting ({sightings.length})
        </button>
        <button
          onClick={() => setActiveTab('marketplace')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'marketplace' 
              ? 'bg-emerald-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ShoppingCart size={18}/>
          Marketplace ({listings.length})
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'settings' 
              ? 'bg-green-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          ⚙️ Ustawienia
        </button>
      </div>

      {/* SPOTTING TAB */}
      {activeTab === 'spotting' && (
        <div className="space-y-4">
          {selectedSighting && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold">Komentarze</h2>
                <button onClick={() => setSelectedSighting(null)} className="text-sm text-blue-600 hover:text-blue-700">
                  Zamknij
                </button>
              </div>
              <div className="space-y-2">
                {comments.map(c => (
                  <div key={c.id} className="bg-white p-3 rounded-lg flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm">{c.text}</p>
                      <p className="text-xs text-gray-500 mt-1">Spotter-{c.userId?.substring(0,4)}</p>
                    </div>
                    <button 
                      onClick={() => deleteComment(selectedSighting, c.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={16}/>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sightings.map(s => (
            <div key={s.id} className={`bg-white border-2 p-4 rounded-xl flex gap-4 ${
              s.hidden ? 'opacity-40 grayscale' : ''
            } ${
              s.isPinned ? 'border-amber-400 bg-amber-50' : 'border-gray-200'
            }`}>
              <img src={s.imageUrl} className="w-24 h-24 rounded-lg object-cover shrink-0" alt="c"/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {s.isPinned && (
                    <span className="px-2 py-1 bg-amber-500 text-white rounded text-[10px] font-bold flex items-center gap-1">
                      <Pin size={10}/> PRZYPIĘTE
                    </span>
                  )}
                  <span className="font-mono font-bold text-lg">{s.plate}</span>
                  
                  {/* BRAND DISPLAY */}
                  {s.brand && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-[10px] font-bold">
                      {s.brand}
                    </span>
                  )}
                  
                  <span className={`px-2 py-1 rounded text-[10px] font-bold ${s.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {s.status === 'verified' ? 'Zweryfikowano' : 'Pending'}
                  </span>
                  
                  {/* COUNTRY EDIT */}
                  {editCountry === s.id ? (
                    <input
                      type="text"
                      placeholder="PL"
                      maxLength={2}
                      className="w-12 px-2 py-1 text-xs border border-blue-500 rounded uppercase text-center font-bold outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSpottingAction(s.id, 'country', e.target.value);
                        if (e.key === 'Escape') setEditCountry(null);
                      }}
                      onBlur={(e) => {
                        if (e.target.value) handleSpottingAction(s.id, 'country', e.target.value);
                        else setEditCountry(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <button onClick={() => setEditCountry(s.id)} className="text-xl hover:scale-110 transition-transform">
                      {s.countryEmoji || "🇵🇱"}
                    </button>
                  )}
                  
                  <button 
                    onClick={() => setSelectedSighting(s.id)}
                    className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                  >
                    💬 {s.commentCount || 0}
                  </button>
                </div>
                
                {/* VIN DISPLAY/EDIT */}
                {(s.vin || editingVin === s.id) && (
                  <div className="mb-2">
                    {editingVin === s.id ? (
                      <input
                        type="text"
                        placeholder="VIN (17 znaków)"
                        maxLength={17}
                        className="w-full px-2 py-1 text-xs border border-blue-500 rounded uppercase font-mono font-bold outline-none"
                        value={vinValue}
                        onChange={(e) => setVinValue(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSpottingAction(s.id, 'vin');
                          if (e.key === 'Escape') { setEditingVin(null); setVinValue(""); }
                        }}
                        onBlur={() => {
                          if (vinValue) handleSpottingAction(s.id, 'vin');
                          else { setEditingVin(null); setVinValue(""); }
                        }}
                        autoFocus
                      />
                    ) : (
                      <button
                        onClick={() => { setEditingVin(s.id); setVinValue(s.vin || ""); }}
                        className="text-xs font-mono font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-1 rounded"
                      >
                        VIN: {s.vin}
                      </button>
                    )}
                  </div>
                )}
                
                {s.reported && (
                  <p className="text-xs bg-red-100 text-red-600 p-2 rounded-lg mb-2">
                    🚩 {s.reportReason}
                  </p>
                )}
                
                {/* DESCRIPTION */}
                <p className="text-sm text-gray-600 line-clamp-2 mb-1">"{s.description}"</p>
                
                {/* ADD VIN BUTTON */}
                {!s.vin && editingVin !== s.id && (
                  <button
                    onClick={() => setEditingVin(s.id)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    + Dodaj VIN
                  </button>
                )}
              </div>
              
              <div className="flex flex-col gap-2 shrink-0">
                <button 
                  onClick={() => handleSpottingAction(s.id, 'pin', s.isPinned)} 
                  className={`p-2 rounded-lg ${s.isPinned ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}
                  title={s.isPinned ? "Odepnij" : "Przypnij na górze"}
                >
                  <Pin size={18} fill={s.isPinned ? "currentColor" : "none"}/>
                </button>
                <button 
                  onClick={() => handleSpottingAction(s.id, 'alert', s.isAlert)} 
                  className={`p-2 rounded-lg ${s.isAlert ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}
                >
                  <AlertTriangle size={18} fill={s.isAlert ? "currentColor" : "none"}/>
                </button>
                <button 
                  onClick={() => handleSpottingAction(s.id, 'exclusive', s.isExclusive)} 
                  className={`p-2 rounded-lg ${s.isExclusive ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}
                >
                  <Star size={18} fill={s.isExclusive ? "currentColor" : "none"}/>
                </button>
                <button 
                  onClick={() => handleSpottingAction(s.id, 'hide', s.hidden)} 
                  className={`p-2 rounded-lg ${s.hidden ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}
                >
                  {s.hidden ? <Eye size={18}/> : <EyeOff size={18}/>}
                </button>
                {s.status === 'verified' ? 
                  <button onClick={() => handleSpottingAction(s.id, 'undo')} className="p-2 bg-gray-100 text-gray-400 rounded-lg">
                    <RotateCcw size={18}/>
                  </button> :
                  <button onClick={() => handleSpottingAction(s.id, 'ok')} className="p-2 bg-green-100 text-green-600 rounded-lg">
                    <CheckCircle size={18}/>
                  </button>
                }
                <button onClick={() => handleSpottingAction(s.id, 'del')} className="p-2 bg-red-100 text-red-600 rounded-lg">
                  <Trash2 size={18}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MARKETPLACE TAB */}
      {activeTab === 'marketplace' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20}/>
              <input
                type="text"
                placeholder="Szukaj po tytule, marce, email, telefonie..."
                className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {selectedListing && (
            <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold">Wiadomości do ogłoszenia (ID: {selectedListing.substring(0, 8)})</h2>
                <button onClick={() => setSelectedListing(null)} className="text-sm text-emerald-600 hover:text-emerald-700">
                  Zamknij
                </button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {listingMessages.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Brak wiadomości dla tego ogłoszenia.<br/>
                    <span className="text-xs">Sprawdź czy użytkownicy napisali do sprzedawcy.</span>
                  </p>
                ) : (
                  listingMessages.map(msg => (
                    <div key={msg.id} className="bg-white p-3 rounded-lg flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-semibold">{msg.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Od: User-{msg.fromUser?.substring(0,6)} → Do: User-{msg.toUser?.substring(0,6)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {msg.createdAt && new Date(msg.createdAt.seconds * 1000).toLocaleString('pl-PL')}
                        </p>
                        {msg.isOffer && (
                          <span className="inline-block mt-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">
                            💰 Oferta: {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }).format(msg.offerPrice || 0)}
                          </span>
                        )}
                      </div>
                      <button 
                        onClick={() => deleteListingMessage(msg.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {filteredListings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShoppingCart className="mx-auto mb-4" size={48}/>
              <p className="font-semibold">Brak ogłoszeń</p>
            </div>
          ) : (
            filteredListings.map(listing => {
              const convCount = getConversationCount(listing.id);
              const currentFlags = listing.flags || [];
              return (
                <div key={listing.id} className={`bg-white border-2 p-4 rounded-xl ${
                  listing.status === 'hidden' ? 'opacity-50' : ''
                } ${
                  listing.isPinned ? 'border-amber-400 bg-amber-50' : 'border-gray-200'
                }`}>
                  <div className="flex gap-4">
                    <img 
                      src={listing.images?.[0] || '/placeholder.jpg'} 
                      className="w-32 h-32 rounded-lg object-cover shrink-0" 
                      alt={listing.title}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            {listing.isPinned && (
                              <span className="px-2 py-1 bg-amber-500 text-white rounded text-[10px] font-bold flex items-center gap-1">
                                <Pin size={10}/> PRZYPIĘTE
                              </span>
                            )}
                            {currentFlags.includes('okazja') && (
                              <span className="px-2 py-1 bg-red-500 text-white rounded text-[10px] font-bold">
                                🔥 OKAZJA
                              </span>
                            )}
                            {currentFlags.includes('pilne') && (
                              <span className="px-2 py-1 bg-yellow-500 text-white rounded text-[10px] font-bold">
                                ⚡ PILNE
                              </span>
                            )}
                            {currentFlags.includes('polecane') && (
                              <span className="px-2 py-1 bg-blue-500 text-white rounded text-[10px] font-bold">
                                ⭐ POLECANE
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-lg text-gray-900">{listing.title}</h3>
                          <p className="text-2xl font-black text-emerald-600">
                            {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }).format(listing.price)}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            listing.status === 'active' ? 'bg-green-100 text-green-700' : 
                            listing.status === 'pending_verification' ? 'bg-amber-100 text-amber-700 flex items-center gap-1' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {listing.status === 'active' && '✅ Aktywne'}
                            {listing.status === 'pending_verification' && (
                              <>
                                <Clock size={12}/>
                                Czeka na weryfikację email
                              </>
                            )}
                            {listing.status === 'hidden' && 'Ukryte'}
                          </span>
                          {listing.manuallyActivated && (
                            <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700">
                              🛡️ Aktywowane przez admina
                            </span>
                          )}
                          {listing.reported && (
                            <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700 flex items-center gap-1">
                              <Flag size={12}/> Oflagowane
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                        <p><strong>Rok:</strong> {listing.year}</p>
                        <p><strong>Przebieg:</strong> {new Intl.NumberFormat('pl-PL').format(listing.mileage)} km</p>
                        <p><strong>Email:</strong> {listing.email}</p>
                        <p><strong>Telefon:</strong> {listing.phone}</p>
                        <p><strong>Lokalizacja:</strong> {listing.location?.name}</p>
                        <p><strong>VIN:</strong> {listing.vin || 'Brak'}</p>
                        <p><strong>Wygasa:</strong> {listing.expiresAt ? new Date(listing.expiresAt).toLocaleDateString('pl-PL') : 'Brak'}</p>
                        <p><strong>Konwersacje:</strong> {convCount}</p>
                      </div>

                      <p className="text-sm text-gray-700 line-clamp-2 mb-3">"{listing.description}"</p>

                      {editingFlags === listing.id && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs font-bold text-gray-700 mb-2">Flagi promocyjne:</p>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => handleListingAction(listing.id, 'toggle_flag', 'okazja', listing)}
                              className={`px-3 py-1 rounded text-xs font-bold ${
                                currentFlags.includes('okazja') 
                                  ? 'bg-red-500 text-white' 
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                            >
                              🔥 Okazja
                            </button>
                            <button
                              onClick={() => handleListingAction(listing.id, 'toggle_flag', 'pilne', listing)}
                              className={`px-3 py-1 rounded text-xs font-bold ${
                                currentFlags.includes('pilne') 
                                  ? 'bg-yellow-500 text-white' 
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                            >
                              ⚡ Pilne
                            </button>
                            <button
                              onClick={() => handleListingAction(listing.id, 'toggle_flag', 'polecane', listing)}
                              className={`px-3 py-1 rounded text-xs font-bold ${
                                currentFlags.includes('polecane') 
                                  ? 'bg-blue-500 text-white' 
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                            >
                              ⭐ Polecane
                            </button>
                            <button
                              onClick={() => setEditingFlags(null)}
                              className="px-3 py-1 bg-green-500 text-white rounded text-xs font-bold"
                            >
                              Zamknij
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleListingAction(listing.id, 'pin', listing.isPinned)}
                          className={`px-3 py-1 rounded text-xs font-semibold flex items-center gap-1 ${
                            listing.isPinned 
                              ? 'bg-amber-100 hover:bg-amber-200 text-amber-700' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <Pin size={14}/> {listing.isPinned ? 'Odepnij' : 'Przypnij'}
                        </button>

                        <button
                          onClick={() => setEditingFlags(editingFlags === listing.id ? null : listing.id)}
                          className="px-3 py-1 bg-purple-100 hover:bg-purple-200 rounded text-xs font-semibold text-purple-700 flex items-center gap-1"
                        >
                          <Tag size={14}/> Flagi ({currentFlags.length})
                        </button>

                        <button
                          onClick={() => setSelectedListing(listing.id)}
                          className="px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded text-xs font-semibold text-blue-700 flex items-center gap-1"
                        >
                          <MessageCircle size={14}/> Wiadomości ({convCount})
                        </button>
                        
                        {listing.status === 'pending_verification' && (
                          <button
                            onClick={() => handleListingAction(listing.id, 'activate_manual')}
                            className="px-3 py-1 bg-green-100 hover:bg-green-200 rounded text-xs font-semibold text-green-700 flex items-center gap-1"
                          >
                            <CheckCircle size={14}/> AKTYWUJ RĘCZNIE
                          </button>
                        )}

                        {listing.status !== 'active' && listing.status !== 'pending_verification' && (
                          <button
                            onClick={() => handleListingAction(listing.id, 'activate')}
                            className="px-3 py-1 bg-green-100 hover:bg-green-200 rounded text-xs font-semibold text-green-700 flex items-center gap-1"
                          >
                            <CheckCircle size={14}/> Aktywuj
                          </button>
                        )}

                        <button
                          onClick={() => handleListingAction(listing.id, 'hide', listing.status)}
                          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs font-semibold text-gray-700 flex items-center gap-1"
                        >
                          {listing.status === 'active' ? <EyeOff size={14}/> : <Eye size={14}/>}
                          {listing.status === 'active' ? 'Ukryj' : 'Pokaż'}
                        </button>

                        {!listing.reported ? (
                          <button
                            onClick={() => handleListingAction(listing.id, 'flag')}
                            className="px-3 py-1 bg-amber-100 hover:bg-amber-200 rounded text-xs font-semibold text-amber-700 flex items-center gap-1"
                          >
                            <Flag size={14}/> Oflaguj
                          </button>
                        ) : (
                          <button
                            onClick={() => handleListingAction(listing.id, 'unflag')}
                            className="px-3 py-1 bg-amber-100 hover:bg-amber-200 rounded text-xs font-semibold text-amber-700 flex items-center gap-1"
                          >
                            <Flag size={14}/> Usuń flagę
                          </button>
                        )}

                        <button
                          onClick={() => handleListingAction(listing.id, 'delete')}
                          className="px-3 py-1 bg-red-100 hover:bg-red-200 rounded text-xs font-semibold text-red-700 flex items-center gap-1"
                        >
                          <Trash2 size={14}/> Usuń
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-xl p-6">
            <h2 className="font-bold mb-4 flex items-center gap-2 text-lg">
              ⚙️ Ustawienia Strony
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nazwa strony (title)
                </label>
                <input
                  type="text"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  className="w-full p-3 bg-white border border-green-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="CarWatch"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SEO Keywords (meta keywords)
                </label>
                <input
                  type="text"
                  value={seoKeywords}
                  onChange={(e) => setSeoKeywords(e.target.value)}
                  className="w-full p-3 bg-white border border-green-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="samochody, auta, marketplace, spotting, ogłoszenia"
                />
                <p className="text-xs text-gray-500 mt-1">Oddzielone przecinkami</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SEO Description (meta description)
                </label>
                <textarea
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  rows={3}
                  className="w-full p-3 bg-white border border-green-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  placeholder="Marketplace i spotting samochodów. Kup lub sprzedaj auto szybko i wygodnie."
                />
                <p className="text-xs text-gray-500 mt-1">Max 160 znaków dla Google</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Regulamin (treść)
                </label>
                <textarea
                  value={termsContent}
                  onChange={(e) => setTermsContent(e.target.value)}
                  rows={10}
                  className="w-full p-3 bg-white border border-green-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500 resize-none font-mono"
                  placeholder="**§1 Postanowienia ogólne**..."
                />
                <p className="text-xs text-gray-500 mt-1">Podstawowe formatowanie: **pogrubienie**, *kursywa*</p>
              </div>

              <button
                onClick={handleSaveSettings}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Zapisz Ustawienia
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <h2 className="font-bold mb-4 flex items-center gap-2">
                <Upload size={20}/>
                Zmień logo aplikacji
              </h2>
              <div className="space-y-3">
                <label className="cursor-pointer block">
                  <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 hover:border-blue-500 transition-all text-center">
                    {logoFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <img 
                          src={URL.createObjectURL(logoFile)} 
                          alt="Preview" 
                          className="h-16 w-16 object-contain"
                        />
                        <span className="text-sm font-semibold text-gray-700">{logoFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        <Upload className="mx-auto mb-2" size={24}/>
                        Kliknij aby wybrać logo
                      </div>
                    )}
                  </div>
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={(e) => setLogoFile(e.target.files[0])}
                  />
                </label>
                <button
                  onClick={handleLogoUpload}
                  disabled={!logoFile || uploadingLogo}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingLogo ? <Loader2 className="animate-spin mx-auto" size={20}/> : "Zapisz Logo"}
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
              <h2 className="font-bold mb-4 flex items-center gap-2">
                <ImageIcon size={20}/>
                Zmień watermark
              </h2>
              <div className="space-y-3">
                <label className="cursor-pointer block">
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 hover:border-purple-500 transition-all text-center">
                    {watermarkFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <img 
                          src={URL.createObjectURL(watermarkFile)} 
                          alt="Preview" 
                          className="h-16 w-16 object-contain"
                        />
                        <span className="text-sm font-semibold text-gray-700">{watermarkFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        <ImageIcon className="mx-auto mb-2" size={24}/>
                        Kliknij aby wybrać watermark
                      </div>
                    )}
                  </div>
                  <input 
                    type="file" 
                    accept="image/png" 
                    className="hidden"
                    onChange={(e) => setWatermarkFile(e.target.files[0])}
                  />
                </label>
                <button
                  onClick={handleWatermarkUpload}
                  disabled={!watermarkFile || uploadingWatermark}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingWatermark ? <Loader2 className="animate-spin mx-auto" size={20}/> : "Zapisz Watermark"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};